import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import InfoTooltip from "Shared/InfoTooltip";
import LoadingBar from "Shared/LoadingBar";
import styled from "styled-components";
import { analyticsFEA } from "src/AnalyticsFEA";
import TradeInEstimateInput from "src/components/shared/CurrencyInput/TradeInEstimateInput";
import Condition from "src/components/TradePending/Inputs/Condition";
import OptionSelectionTool from "src/components/TradePending/Inputs/OptionSelectionTool";
import RemainingBalance from "src/components/TradePending/Inputs/RemainingBalance";
import {
    deleteTradeInRequest,
    getAndPatchTradePendingReportRequest,
    patchTradeInRequest,
    toggleTradeInDrawer,
    toggleTradeInEstimateModeManual,
    toggleTradeInEstimateModeTradePending,
} from "src/store/actions/tradeIn";
import { EstimateMode, Status, VehicleInputMode } from "src/store/reducers/tradeIn";
import { getZipCode } from "src/store/selectors/person";
import { getVehiclesArrayForFEA } from "src/store/selectors/vehicles";
import { ApplicationStore } from "Types/state";
import { Distance, TradeIn, Yn } from "Types/TradeIn";
import Footer from "../../containers/TradePendingFooter";
import ManualInputs from "./Inputs/ManualInputs";
import Odometer from "./Inputs/Odometer";
import VehicleSearch from "./Inputs/VehicleSearch";
import VehicleFound from "./Inputs/VehicleSearch/VehicleFound";
import "./TradePending.scss";
import { TradePendingHeader } from "src/components/TradePending/Elements/Header";
import { ManualEstimate } from "src/components/ManualTradeEstimate/ManualEstimate";
import { TradePendingEstimateToggle } from "src/components/TradePending/Elements/TradeInEstimateToggle";
import { TradePendingDoneButton } from "src/components/TradePending/Elements/TradePendingDoneBtn";
import { getAccountZipCode } from "src/store/selectors/account";

const SEARCH_INPUT_HASH = "tradePendingSearchInput";
const MANUAL_INPUTS_HASH = "tradePendingManualInputs";
const ODOMETER_INPUT_HASH = "tradePendingOdometerInput";
const REMAINING_BALANCE_INPUT_HASH = "tradePendingRemainingBalanceInput";
const OPTION_SELECTION_TOOL_HASH = "tradePendingOptionSelectionToolSelect";

export const Root = styled("div")<{ isDrawer: boolean }>`
    background-color: #f9f9f9;
    color: ${(props) => props.theme.palette.common.black};
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin: ${(props) => (props.isDrawer ? "0" : "0 15px 20px")};
    max-width: 100%;
    min-height: 65px;
    padding: 0;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 0 0 20px;
    }
`;

const VehicleSearchContainer = styled("div")`
    max-height: 130px;
`;

interface AdditionalProps {
    isDrawer?: boolean;
    onFocus?: (e: React.FocusEvent<HTMLElement>, linkHash: string) => void;
}

const TradePending = ({
    hasTradeIn,
    hasAddedTradeInVehicle,
    year,
    make,
    model,
    trim,
    balance,
    tradePendingVehicleId,
    odometerAmount,
    odometerUnits,
    yearMakeModelTrim,
    postalCode,
    country,
    toolTipText,
    estimateInputMode,
    vehicleInputMode,
    tp_tradein_low,
    tp_tradein_target,
    tp_tradein_high,
    tp_tradein_report,
    condition,
    status,
    customer_estimate,
    account,
    uuid,
    vehiclesForFEA,
    patchTradeIn,
    deleteTradeIn,
    getTradePendingReport,
    toggleTradeIn,
    toggleTradeInManualModeOn,
    toggleTradeInManualModeOff,
    onFocus,
    isDrawer,
    choices,
    question,
    answers,
    partnerId,
    url,
    errorMessage,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & AdditionalProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    const titleRef = useRef<HTMLDivElement>(null);
    const odometerRef = useRef<HTMLDivElement>(null);
    const vehicleSearchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (hasTradeIn && !hasAddedTradeInVehicle) {
            vehicleSearchRef.current?.focus();
            vehicleSearchRef.current?.querySelector("input")?.focus();
            analyticsFEA.engine.tradeInStart(uuid, vehiclesForFEA);
        }
    }, [hasTradeIn, hasAddedTradeInVehicle, uuid, vehiclesForFEA]);

    useEffect(() => {
        if (yearMakeModelTrim && !odometerAmount) {
            odometerRef.current?.querySelector("input")?.focus();
            titleRef.current?.scrollIntoView();
        }
    }, [yearMakeModelTrim, odometerAmount]);

    useEffect(() => {
        if (
            tradePendingVehicleId &&
            odometerAmount !== undefined &&
            odometerUnits &&
            tp_tradein_report === undefined &&
            partnerId
        ) {
            getTradePendingReport({
                uuid,
                tradeIn: {
                    id: tradePendingVehicleId,
                    miles: odometerAmount,
                },
                postalCode,
                partnerId,
                url,
                vehiclesForFEA,
                initiator: "user",
            });
        }
    }, [
        tradePendingVehicleId,
        postalCode,
        odometerAmount,
        odometerUnits,
        uuid,
        getTradePendingReport,
        partnerId,
        url,
        vehiclesForFEA,
        tp_tradein_report,
        country,
    ]);

    const handleOnChange = (name: string, value: "yes" | "no") => {
        if (value === "no") {
            deleteTradeIn(uuid);
            return;
        }

        patchTradeIn(
            {
                yn: Yn.Yes,
            },
            uuid,
            "start_trade_in"
        );
    };

    const handleManualCustomerEstimateChange = (value: number) => {
        patchTradeIn(
            {
                customer_estimate: value,
            },
            uuid,
            "finish_trade_in",
            "manual"
        );
    };

    const handleModeChange = () => {
        if (estimateInputMode === EstimateMode.Manual) {
            toggleTradeInManualModeOff();
            return;
        }

        toggleTradeInManualModeOn();
    };

    const handleOdometerChange = (tradeInPatch: Partial<TradeIn>) => {
        patchTradeIn(tradeInPatch, uuid);
    };

    const handleConditionChange = (tradeInPatch: Partial<TradeIn>) => {
        patchTradeIn(
            {
                condition: tradeInPatch.condition,
                customer_estimate: tradeInPatch.customer_estimate,
            },
            uuid
        );
        analyticsFEA.engine.tradeInComplete(uuid, vehiclesForFEA);
    };

    const handleRemainingBalanceChange = ({ balance: value }: Pick<TradeIn, "balance">) => {
        patchTradeIn({ balance: value || 0 }, uuid);
    };

    const handleTradePendingDone = () => {
        toggleTradeIn();
        history.push("/my-garage", history.location.state);
    };

    const handleVehicleSearchFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        onFocus && onFocus(e, SEARCH_INPUT_HASH);
    };

    const handleManualInputsFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        onFocus && onFocus(e, MANUAL_INPUTS_HASH);
    };

    const handleOdometerFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        onFocus && onFocus(e, ODOMETER_INPUT_HASH);
    };

    const handleRemainingBalanceFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        onFocus && onFocus(e, REMAINING_BALANCE_INPUT_HASH);
    };

    const handleOptionSelectionToolFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
        onFocus && onFocus(e, OPTION_SELECTION_TOOL_HASH);
    };
    if (!partnerId && hasTradeIn) {
        return (
            <Root isDrawer={!!isDrawer}>
                <TradePendingHeader onChange={handleOnChange} />
                <ManualEstimate
                    isDrawer={!!isDrawer}
                    tradePending={{ year, make, model, trim, customer_estimate }}
                    uuid={uuid}
                    onChangePatchTradePending={patchTradeIn}
                />
                {isDrawer && <TradePendingDoneButton onClick={handleTradePendingDone} />}
            </Root>
        );
    }

    return (
        <Root isDrawer={!!isDrawer}>
            <TradePendingHeader onChange={handleOnChange} />

            {hasTradeIn && estimateInputMode === EstimateMode.TradePending && (
                <div className="TradePending__body--container">
                    <div className="TradePending__body--vehicle" ref={vehicleSearchRef}>
                        <div id={SEARCH_INPUT_HASH} />
                        {t("TradePending.whatVehicle")}
                        {!hasAddedTradeInVehicle && (
                            <VehicleSearchContainer>
                                <VehicleSearch onFocus={handleVehicleSearchFocus} />
                            </VehicleSearchContainer>
                        )}
                        {hasAddedTradeInVehicle && <VehicleFound tradePending={{ ymmt: yearMakeModelTrim }} />}

                        {vehicleInputMode === VehicleInputMode.Fallback && (
                            <React.Fragment>
                                <div id={MANUAL_INPUTS_HASH} />
                                <ManualInputs
                                    tradePending={{ year, make, model, trim }}
                                    uuid={uuid}
                                    patchTradePending={patchTradeIn}
                                    onFocus={handleManualInputsFocus}
                                />
                            </React.Fragment>
                        )}
                    </div>

                    {vehicleInputMode === VehicleInputMode.Default && (
                        <React.Fragment>
                            {hasAddedTradeInVehicle && (
                                <React.Fragment>
                                    <div className="TradePending__body--odometer" ref={odometerRef}>
                                        <div id={ODOMETER_INPUT_HASH} />
                                        <Odometer
                                            region={country}
                                            tradePending={{
                                                model,
                                                odometer: { amount: odometerAmount, unit: odometerUnits } as Distance,
                                            }}
                                            onChange={handleOdometerChange}
                                            onFocus={handleOdometerFocus}
                                        />
                                    </div>
                                    <div className="TradePending__body--remainingBalance">
                                        <div id={REMAINING_BALANCE_INPUT_HASH} />
                                        <RemainingBalance
                                            balance={balance}
                                            onChange={handleRemainingBalanceChange}
                                            onFocus={handleRemainingBalanceFocus}
                                        />
                                    </div>
                                </React.Fragment>
                            )}
                            {odometerAmount !== undefined && ((question && choices) || answers) && (
                                <div>
                                    <div id={OPTION_SELECTION_TOOL_HASH} />
                                    <OptionSelectionTool onFocus={handleOptionSelectionToolFocus} />
                                </div>
                            )}
                            {odometerAmount !== undefined &&
                                (tp_tradein_low || tp_tradein_target || tp_tradein_high || errorMessage) &&
                                status !== Status.Loading && (
                                    <Condition
                                        tradePending={{
                                            tp_tradein_low,
                                            tp_tradein_target,
                                            tp_tradein_high,
                                            condition,
                                            errorMessage,
                                            model,
                                        }}
                                        setSelect={handleConditionChange}
                                        uuid={uuid}
                                        vehicles={vehiclesForFEA}
                                    />
                                )}
                        </React.Fragment>
                    )}

                    {status === Status.Loading && odometerAmount !== undefined && (
                        <div className="TradePending__loadingComponent">
                            <LoadingBar />
                        </div>
                    )}

                    <Footer account={account} />

                    {isDrawer && <TradePendingDoneButton onClick={handleTradePendingDone} />}
                </div>
            )}

            {hasTradeIn && estimateInputMode === EstimateMode.Manual && (
                <div className="TradeInEstimateRow">
                    <div className={`TradeInEstimateRow__head ${isDrawer && "DrawerMode"}`}>
                        {t("MyGarage.GlobalPaymentsControl.TradePendingRow.header")}

                        <div className="TradeInEstimateRow__container--toolTip">
                            <InfoTooltip className="TradeInEstimateRow__toolTip" icon="info" text={toolTipText} />
                        </div>
                    </div>
                    <div className="TradeInEstimateRow__body">
                        <TradeInEstimateInput
                            initialValue={customer_estimate}
                            updateEstimate={handleManualCustomerEstimateChange}
                        />
                    </div>
                </div>
            )}

            {hasTradeIn && (
                <TradePendingEstimateToggle onClick={handleModeChange} estimateInputMode={estimateInputMode} />
            )}
        </Root>
    );
};

const mapStateToProps = (state: ApplicationStore) => ({
    hasTradeIn: state.tradeIn.yn === Yn.Yes,
    hasAddedTradeInVehicle: state.tradeIn.ymmt !== undefined,
    year: state.tradeIn.year,
    make: state.tradeIn.make,
    model: state.tradeIn.model,
    trim: state.tradeIn.trim,
    balance: state.tradeIn.balance || 0,
    status: state.tradeIn.status,
    estimateInputMode: state.tradeIn.estimateMode,
    vehicleInputMode: state.tradeIn.vehicleInputMode,
    tp_tradein_high: state.tradeIn.tp_tradein_high,
    tp_tradein_target: state.tradeIn.tp_tradein_target,
    tp_tradein_low: state.tradeIn.tp_tradein_low,
    tp_tradein_report: state.tradeIn.tp_tradein_report,
    condition: state.tradeIn.condition,
    customer_estimate: state.tradeIn.customer_estimate ?? 0,
    choices: state.tradeIn.choices,
    question: state.tradeIn.question,
    answers: state.tradeIn.answers,
    odometerAmount: state.tradeIn.odometer?.amount,
    odometerUnits: state.tradeIn.odometer?.unit,
    tradePendingVehicleId: state.tradeIn.id,
    yearMakeModelTrim: state.tradeIn.ymmt,
    postalCode: getZipCode(state) ?? getAccountZipCode(state),
    country: state.account.address?.country ?? "US",
    toolTipText: state.account.account_disclaimers?.global_trade_in,
    uuid: state.deal.uuid as string,
    vehiclesForFEA: getVehiclesArrayForFEA(state),
    account: state.account,
    partnerId: state.account.integration_settings?.TP_TradeEstimate?.partner_id,
    url: state.account.url,
    errorMessage: state.tradeIn.errorMessage,
});

const mapDispatchToProps = {
    patchTradeIn: patchTradeInRequest,
    deleteTradeIn: deleteTradeInRequest,
    getTradePendingReport: getAndPatchTradePendingReportRequest,
    toggleTradeIn: toggleTradeInDrawer,
    toggleTradeInManualModeOn: toggleTradeInEstimateModeManual,
    toggleTradeInManualModeOff: toggleTradeInEstimateModeTradePending,
};

export default connect(mapStateToProps, mapDispatchToProps)(TradePending);
